import { Add16 } from '@carbon/icons-react';
import { SelectionChangedEvent, GetRowIdParams } from 'ag-grid-community';
import moment from 'moment';
import { pipe } from 'ramda';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Meeting } from 'api/meeting/types';
import { Button } from 'components/atoms/button';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
} from 'components/atoms/drawer';
import { Grid as GridAtom } from 'components/atoms/grid/grid';
import { withExternalFilter } from 'components/atoms/grid/plugins/with-external-filter';
import { humanise } from 'components/atoms/humanised-date';
import { SearchInput } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { FilterBar } from 'components/molecules/filter-bar';
import { useTranslate } from 'hooks/use-translation';
import { useGetAllMeetings } from 'queries/meeting';
import { sortByText } from 'utils/sort-by-text';
import {
  CheckboxRenderer,
  MeetingTypeRenderer,
  MeetingsEmptyState,
  MeetingsNoSearchResultState,
  ParticipantsRenderer,
} from './renderers';

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;
const DrawerContent = styled(CustomLayoutDrawerContent)`
  ${CustomLayoutDrawerContent.Content} {
    padding: 0 !important;
  }
  ${CustomLayoutDrawerContent.Footer} {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-05);
  }
`;
const GridContainer = styled.div`
  height: 100%;
  width: 100%;
  padding-left: var(--spacing-05);
  position: relative;
`;

const Grid = styled(GridAtom)`
  .ag-react-container {
    > :not(:first-child) {
      display: none !important;
    }
    width: 100%;
    > div {
      width: 100%;
    }
  }
  .ag-row {
    border-bottom: 1px solid var(--border-color);
    .ag-selection-checkbox {
      opacity: 1;
    }
  }
  .ag-row-odd {
    background-color: var(--ag-background-color, var(--white-100));
  }
  .ag-row-focus {
    background-color: var(--ag-background-color, var(--white-100));
  }
  .ag-row-hover {
    background: var(--ag-row-hover-color);
  }
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
    align-items: flex-start;
    .ag-react-container {
      padding-left: var(--layout-06);
      padding-top: var(--layout-06);
    }
  }
  .ag-cell {
    padding: 0 var(--cell-padding);
  }
`;
const FilterableGrid = pipe(pipe(() => Grid, withExternalFilter))();

const onModelUpdated = ({ api }) => {
  if (api.getDisplayedRowCount() === 0) {
    api.showNoRowsOverlay();
  } else {
    api.hideOverlay();
  }
};

interface Props {
  onClose: VoidFunction;
  onGenerate: (meetings: Meeting[]) => void;
}
export const MeetingsDrawer: FC<Props> = ({ onClose, onGenerate }) => {
  const translate = useTranslate();
  const { data: meetings, isLoading: isMeetingsLoading } = useGetAllMeetings();
  const columns = useMemo(
    () => [
      {
        field: 'checkbox',
        headerName: '',
        lockPinned: true,
        lockPosition: true,
        initialWidth: 40,
        // checkboxSelection: true,
        cellStyle: { borderColor: 'transparent !important' },
        cellRenderer: 'CheckboxRenderer',
      },
      {
        flex: 1,
        field: 'meetingName',
        headerName: translate(
          'ai_fill.meetings.select.grid.column.meeting_name',
        ),
        sortable: true,
        comparator: sortByText,
      },
      {
        width: 120,
        field: 'startTime',
        headerName: translate('ai_fill.meetings.select.grid.column.date'),
        sortable: true,
        valueFormatter: ({ value }) => humanise(moment(value)),
        comparator: sortByText,
      },
      {
        width: 120,
        field: 'participants',
        headerName: translate(
          'ai_fill.meetings.select.grid.column.participants',
        ),
        sortable: false,
        cellRenderer: 'ParticipantsRenderer',
      },
      {
        width: 144,
        field: 'meetingType',
        headerName: translate(
          'ai_fill.meetings.select.grid.column.meeting_type',
        ),
        sortable: true,
        cellRenderer: MeetingTypeRenderer,
        comparator: (valueA, valueB) => sortByText(valueA.label, valueB.label),
      },
    ],
    [translate],
  );
  const [searchText, setSearchText] = useState('');
  const filterFunction = useCallback(
    (meeting) =>
      meeting?.meetingName
        ?.toLowerCase()
        .includes(searchText.toLowerCase().trim()) ?? false,
    [searchText],
  );
  const [selected, setSelected] = useState<Meeting[]>([]);
  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent) => {
      const selectedData = api.getSelectedNodes().reduce((acc, node) => {
        if (node.id != null) {
          acc.push(node.data);
        }

        return acc;
      }, [] as Meeting[]);

      setSelected(selectedData);
      api.redrawRows();
    },
    [setSelected],
  );

  return (
    <CustomLayoutDrawer
      {...DefaultCustomLayoutDrawerProps}
      width={680}
      visible
      onClose={onClose}
    >
      <DrawerContent>
        <CustomLayoutDrawerContent.Title>
          <FormattedMessage id="ai_fill.meetings.select.drawer.title" />
          <CustomLayoutDrawerContent.Title.Description>
            <FormattedMessage id="ai_fill.meetings.select.drawer.description" />
          </CustomLayoutDrawerContent.Title.Description>
        </CustomLayoutDrawerContent.Title>
        {isMeetingsLoading ? (
          <CustomLayoutDrawerContent.Content>
            <LoadingWrapper>
              <Loading size="large" />
            </LoadingWrapper>
          </CustomLayoutDrawerContent.Content>
        ) : (meetings ?? []).length === 0 ? (
          <CustomLayoutDrawerContent.Content>
            <MeetingsEmptyState />
          </CustomLayoutDrawerContent.Content>
        ) : (
          <CustomLayoutDrawerContent.Content>
            <FilterBar>
              <FilterBar.Primary>
                <></>
              </FilterBar.Primary>
              <FilterBar.Secondary>
                <SearchInput
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  direction="right"
                  searchButtonProps={{ type: 'text' }}
                />
              </FilterBar.Secondary>
            </FilterBar>
            <GridContainer className="ag-theme-balham">
              <FilterableGrid
                suppressContextMenu
                rowMultiSelectWithClick
                rowSelection="multiple"
                suppressCellFocus
                getRowId={({ data }: GetRowIdParams) =>
                  data.meetingId.toString()
                }
                defaultColDef={{
                  filter: false,
                  resizable: false,
                  suppressMovable: true,
                  suppressMenu: true,
                  editable: false,
                  unSortIcon: true,
                  wrapText: true,
                }}
                components={{
                  ParticipantsRenderer,
                  MeetingTypeRenderer,
                  CheckboxRenderer,
                  MeetingsNoSearchResultState,
                }}
                filter={filterFunction}
                size="medium"
                rowData={meetings}
                columnDefs={columns}
                onSelectionChanged={onSelectionChanged}
                noRowsOverlayComponent="MeetingsNoSearchResultState"
                onModelUpdated={onModelUpdated}
              />
            </GridContainer>
          </CustomLayoutDrawerContent.Content>
        )}
        <CustomLayoutDrawerContent.Footer>
          <Button type="tertiary" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            type="primary"
            icon={<Add16 />}
            iconPlacement="right"
            disabled={selected.length === 0}
            onClick={() => onGenerate(selected)}
          >
            <FormattedMessage id="generate" />
          </Button>
        </CustomLayoutDrawerContent.Footer>
      </DrawerContent>
    </CustomLayoutDrawer>
  );
};
